import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import Stress from "../../../Assets/Images/Svg/Stress.svg";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Modal, Button } from "antd";

const STRESS_RANGES = [
  { min: 0, max: 50, color: "#1976d2", label: "Normal" },
  { min: 50, max: 75, color: "#fff000", label: "Moderate" },
  { min: 75, max: 90, color: "#F57927", label: "High" },
  { min: 90, max: Infinity, color: "#F52727", label: "Very High" },
];

const StressData = ({ smartWatchId }) => {
  const [stress, setStress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stressDetails, setStressDetails] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (smartWatchId) {
      fetchStressData();
    }
  }, [smartWatchId]);

  const fetchStressData = async (fromDate, toDate) => {
    const start = fromDate
      ? dayjs(fromDate).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");
    const end = toDate
      ? dayjs(toDate).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");

    try {
      setLoading(true);
      const basePayload = {
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "STRESSDETAILS",
      };
      const res = await PatientService.GetGarminDevices(basePayload);
      if (res?.data.length > 0) {
        const parsedData = res?.data?.map((item) => {
          let parsedRaw = {};
          let parsedStress = {};

          try {
            parsedRaw = JSON.parse(item.RAW_JSON);
          } catch (e) {
            console.error("RAW_JSON parsing failed:", e);
          }

          try {
            parsedStress = JSON.parse(item?.stressDetails);
          } catch (e) {
            console.error("stressDetails parsing failed:", e);
          }

          return {
            ...item,
            RAW_JSON: parsedRaw,
            stressDetails: parsedStress,
          };
        });

        setStress(parsedData);
        setStressDetails(parsedData[0]);
      } else {
        setStress([]);
        setStressDetails({});
      }
    } catch (err) {
      console.error("Error fetching Garmin data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  const showGraph = () => {
    if (!smartWatchId) return;
    setIsModalOpen(true);
  };

  const stressSummary = stressDetails?.stressDetails?.StressSummary || {};

  return (
    <div
      title="Click to view Graph"
      className="w-full h-40 bg-white rounded-lg shadow flex flex-col justify-center items-center p-4 cursor-pointer"
      onClick={showGraph}
    >
      <div className="flex items-center gap-2 self-start">
        <img src={Stress} alt="icon" />
        <h2 className="text-sm font-sans font-semibold">Stress</h2>
      </div>

      <div className="relative w-20 h-20">
        <svg className="w-full h-full" viewBox="0 0 36 36">
          <path
            className="stroke-gray-200"
            strokeWidth="3"
            fill="none"
            d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
          />
          <path
            className="stroke-[#f97316]"
            strokeWidth="3"
            strokeDasharray={`${
              (stressSummary.AverageStress / stressSummary.MaxStress) * 100
            }, 100`}
            strokeLinecap="round"
            fill="none"
            d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
          {stressSummary?.AverageStress
            ? Math.round(stressSummary.AverageStress)
            : "--"}
        </span>
      </div>
      <StressModal
        open={isModalOpen}
        onClose={handleClose}
        smartWatchId={smartWatchId}
      />
    </div>
  );
};

const StressModal = ({ open, onClose, smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [chartData, setChartData] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [summary, setSummary] = useState({ average: 0, max: 0 });

  const fetchStressDataByDateRange = async (from, to) => {
    const start = from.format("DD/MM/YYYY");
    const end = to.format("DD/MM/YYYY");

    try {
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 100,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "STRESSDETAILS",
      });
      if (res?.data.length > 0) {
        const parsedData = res.data
          .map((item) => {
            let parsedStress = {};
            let raw = {};
            try {
              parsedStress = JSON.parse(item?.stressDetails || "{}");
              raw = JSON.parse(item?.RAW_JSON || "{}");
            } catch {}
            return {
              date: raw?.calendarDate || "",
              avg: parsedStress?.StressSummary?.AverageStress || 0,
              max: parsedStress?.StressSummary?.MaxStress || 0,
            };
          })
          .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());
        setChartData(parsedData);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrev = () => {
    const newStart = startDate.subtract(7, "day");
    const newEnd = endDate.subtract(7, "day");
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchStressDataByDateRange(newStart, newEnd);
  };

  const handleNext = () => {
    const today = dayjs();
    let newStart = startDate.add(7, "day");
    let newEnd = endDate.add(7, "day");

    if (newEnd.isAfter(today, "day")) {
      newEnd = today;
      newStart = today.subtract(6, "day");
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    fetchStressDataByDateRange(newStart, newEnd);
  };

  useEffect(() => {
    if (tab === "7D") {
      fetchStressDataByDateRange(startDate, endDate);
    }
  }, [tab, startDate, endDate, smartWatchId]);

  const fetch1DData = async (date) => {
    try {
      const formattedDate = date.format("DD/MM/YYYY");

      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: formattedDate,
        toDate: formattedDate,
        type: "STRESSDETAILS",
      });

      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        const stressDetails = JSON.parse(res.data[0].stressDetails || "{}");

        const dateStart = dayjs(Number(res.data[0].CALENDAR_DATE)).startOf(
          "day"
        );

        const timeValues = Object.entries(
          raw?.timeOffsetStressLevelValues || {}
        )
          .filter(([_, val]) => val >= 0)
          .map(([offsetSec, val]) => {
            const time = dateStart.add(Number(offsetSec), "second");
            return {
              time: time.format("HH:mm"),
              value: val,
            };
          });

        const detailedData = timeValues.map((d) => {
          const range = STRESS_RANGES.find(
            (r) => d.value >= r.min && d.value < r.max
          );
          return {
            ...d,
            color: range?.color || "#e0e0e0",
            label: range?.label || "No Data",
          };
        });

        detailedData.sort((a, b) =>
          dayjs(a.time, "HH:mm").isAfter(dayjs(b.time, "HH:mm")) ? 1 : -1
        );

        setChartData(detailedData);

        // ✅ Update summary card
        setSummary({
          average: Math.round(stressDetails?.StressSummary?.AverageStress || 0),
          max: Math.round(stressDetails?.StressSummary?.MaxStress || 0),
        });
      } else {
        setChartData([]);
        setSummary({ average: 0, max: 0 });
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
    }
  }, [tab, selectedDate]);

  // ✅ Date navigation
  const handlePrev1Day = () => {
    setSelectedDate((prev) => prev.subtract(1, "day"));
  };
  const handleNext1Day = () => {
    setSelectedDate((prev) => {
      const nextDate = prev.add(1, "day");
      const today = dayjs().startOf("day");
      if (nextDate.isAfter(today)) {
        return prev;
      }
      return nextDate;
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title="Stress Graph"
    >
      <div>
        <div className="flex justify-center mb-4 gap-3">
          <button
            onClick={() => setTab("1D")}
            className={`${
              tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
            } px-4 py-2 rounded-md`}
          >
            1D
          </button>
          <button
            className={`${
              tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
            } px-4 py-2 rounded-md`}
            onClick={() => setTab("7D")}
          >
            7D
          </button>
        </div>

        {tab === "7D" && (
          <div className="flex justify-between mb-4">
            <button
              className="px-4 py-2 rounded-md text-white"
              onClick={handlePrev}
            >
              ← Prev 7 Days
            </button>
            <span>
              {startDate.format("DD/MM/YYYY")} - {endDate.format("DD/MM/YYYY")}
            </span>
            <button
              className="px-4 py-2 rounded-md text-white"
              onClick={handleNext}
            >
              Next 7 Days →
            </button>
          </div>
        )}

        {tab === "1D" ? (
          <>
            <div className="flex justify-between">
              <div className=" p-2 w-full max-w-md text-center mb-4">
                <div className="flex gap-1 text-sm font-medium">
                  <div className="border p-2 rounded-md border-blue-500">
                    <p className="text-gray-600">Average Stress</p>
                    <p className="text-blue-600 text-lg">{summary.average}</p>
                  </div>
                  <div className="border p-2 rounded-md border-blue-500">
                    <p className="text-gray-600">Max Stress</p>
                    <p className="text-red-600 text-lg">{summary.max}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-6 mb-4">
                <Button shape="circle" onClick={handlePrev1Day}>
                  ←
                </Button>
                <span className="text-base font-semibold text-gray-800">
                  {selectedDate.format("DD MMM YYYY")}
                </span>
                <Button shape="circle" onClick={handleNext1Day}>
                  →
                </Button>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  type="category"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  interval={Math.ceil(chartData.length / 12)}
                />
                <YAxis />
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} (${props.payload.label})`,
                    "Stress Level",
                  ]}
                />
                <Bar dataKey="value" barSize={6} radius={[3, 3, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div className="flex justify-center gap-4 mt-3">
              {STRESS_RANGES.map((range, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: range.color }}
                  ></div>
                  <span className="text-xs font-medium text-gray-700">
                    {range.label}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Graph */}
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => dayjs(d).format("DD/MM/YYYY")}
                  />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="avg"
                    stroke="#f97316"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5 mt-6">
              {chartData.map((item, idx) => {
                const percentage = item.max ? (item.avg / item.max) * 100 : 0;

                return (
                  <div
                    key={idx}
                    className="bg-white p-3 rounded shadow flex flex-col items-center"
                  >
                    <div className="relative w-20 h-20">
                      <svg className="w-full h-full" viewBox="0 0 36 36">
                        <path
                          className="stroke-gray-200"
                          strokeWidth="3"
                          fill="none"
                          d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
                        />
                        <path
                          className="stroke-[#f97316]"
                          strokeWidth="3"
                          strokeDasharray={`${percentage}, 100`}
                          strokeLinecap="round"
                          fill="none"
                          d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
                        {item.avg ? Math.round(item.avg) : "--"}
                      </span>
                    </div>
                    <span className="text-xs font-medium mt-1">
                      {dayjs(item.date).format("DD/MM")}
                    </span>
                    <span className="text-xs text-gray-500">
                      Max: {item.max}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default StressData;
